import React from 'react';
import { Pressable, View } from 'react-native';

import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import InternationalPaymentNotice from '@/components/InternationalPaymentNotice';
import Reveal from '@/components/Landing/Reveal';
import {
  GhostCta,
  Lede,
  Split,
  useLanding,
} from '@/components/Landing/LandingKit';
import { RuledGrid } from '@/components/Landing/MarketingKit';
import { EASE, MONO, fluid, isWeb } from '@/components/Landing/landingTokens';
import { PricingSection, PricingTitle } from './PricingKit';
import {
  CREDIT_EXAMPLES,
  CREDIT_PACKS,
  CREDIT_PILLARS,
} from '@/constants/pricingContent';

/** Per-credit rate, to three decimals — the number that makes packs comparable. */
function rateOf(pack) {
  return (pack.price / pack.credits).toFixed(3);
}

function CreditPack({ pack, onPress }) {
  const { c, band, isCompact } = useLanding();

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Buy ${pack.credits.toLocaleString()} credits for $${pack.price}`}
      style={({ hovered, pressed }) => ({
        // See PricingPlans: a percentage basis is a height once the row stacks.
        flexGrow: isCompact ? 0 : 1,
        flexBasis: isCompact ? 'auto' : 0,
        minWidth: 0,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: hovered ? c.accentRing : band.rule,
        backgroundColor: band.surface,
        padding: 22,
        gap: 14,
        opacity: pressed ? 0.85 : 1,
        transform: [{ translateY: isWeb && hovered ? -3 : 0 }],
        ...(isWeb ? { cursor: 'pointer', transition: `transform 220ms ${EASE}` } : {}),
      })}
    >
      <View style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 7 }}>
          <Text
            weight="bold"
            style={{
              color: band.ink,
              fontSize: 30,
              letterSpacing: -1.2,
              fontVariant: ['tabular-nums'],
            }}
          >
            {pack.credits.toLocaleString()}
          </Text>
          <Text weight="medium" style={{ color: band.muted, fontSize: 13 }}>credits</Text>
        </View>
        {pack.badge ? (
          <Text weight="bold" style={{ color: c.accent, fontSize: 9.5, letterSpacing: 0.3 }}>
            {pack.badge.toUpperCase()}
          </Text>
        ) : null}
      </View>

      <View style={{ height: 1, backgroundColor: band.rule }} />

      <View style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Text weight="bold" style={{ color: band.ink, fontSize: 17, letterSpacing: -0.3 }}>
          ${pack.price}
        </Text>
        <Text style={{ color: band.muted, fontFamily: MONO, fontSize: 11 }}>
          ${rateOf(pack)}/credit
        </Text>
      </View>
    </Pressable>
  );
}

/**
 * How credits work, then what they buy, then how to get more.
 *
 * The worked prices come from each model's configured base cost, so this
 * section answers "what does a credit actually get me" with real numbers
 * instead of the usual hand-wave.
 */
function PricingCredits({ onBuyCredits }) {
  const { c, band, isXS, isCompact, width } = useLanding();

  return (
    <PricingSection label="How credits work" background={band.bg}>
        <Split ratio={[1, 1]} align="flex-start">
          <View style={{ gap: 18 }}>
            <Text weight="medium" style={{ color: c.accent, fontSize: 14 }}>Credits</Text>
            <PricingTitle>One balance. Every model.</PricingTitle>
          </View>

          <Reveal delay={120}>
            <Lede tone="band" max={470} style={{ color: band.inkSoft, fontSize: 16, lineHeight: 27, paddingTop: isCompact ? 0 : 8 }}>
              Plus includes unlimited default-model chat in the app. Credits cover
              other models and tools, from frontier chat to images, video, music,
              and 3D. Use your monthly balance, then top up when you need more.
            </Lede>
          </Reveal>
        </Split>

        <Reveal delay={60}>
          <RuledGrid
            items={CREDIT_PILLARS}
            tone="band"
            style={{ marginTop: fluid(width, 40, 64) }}
            renderItem={(pillar) => (
              <>
                <Icon name={pillar.icon} size={17} color={c.accent} solid />
                <Text weight="bold" style={{ color: band.ink, fontSize: 17, letterSpacing: -0.3 }}>
                  {pillar.title}
                </Text>
                <Text style={{ color: band.muted, fontSize: 14, lineHeight: 22 }}>
                  {pillar.copy}
                </Text>
              </>
            )}
          />
        </Reveal>

        {/* Real prices, so the abstraction lands. */}
        <Reveal delay={80}>
          <View style={{ marginTop: fluid(width, 44, 72) }}>
            <Text weight="bold" style={{ color: band.ink, fontSize: 17, letterSpacing: -0.3 }}>
              What a credit buys
            </Text>

            <View style={{ marginTop: 20, borderTopWidth: 1, borderTopColor: band.rule }}>
              {CREDIT_EXAMPLES.map((example) => (
                <View
                  key={example.label}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 16,
                    paddingVertical: 15,
                    borderBottomWidth: 1,
                    borderBottomColor: band.rule,
                  }}
                >
                  <Text
                    weight="bold"
                    style={{
                      color: c.accent,
                      fontFamily: MONO,
                      fontSize: 13,
                      width: 46,
                      textAlign: 'right',
                      fontVariant: ['tabular-nums'],
                    }}
                  >
                    {example.credits}
                  </Text>
                  <Text
                    weight="medium"
                    style={{ color: band.inkSoft, fontSize: 14, flex: 1, minWidth: 0 }}
                  >
                    {example.label}
                  </Text>
                  {!isXS ? (
                    <Text style={{ color: band.muted, fontSize: 12.5 }}>{example.model}</Text>
                  ) : null}
                </View>
              ))}
            </View>

            <Text style={{ color: band.muted, fontSize: 12.5, marginTop: 14 }}>
              Prices vary by model — the picker shows the cost before you send.
            </Text>
          </View>
        </Reveal>

        {/* Top-ups */}
        <Reveal delay={100}>
          <View style={{ marginTop: fluid(width, 48, 80) }}>
            <View
              style={{
                flexDirection: isCompact ? 'column' : 'row',
                alignItems: isCompact ? 'flex-start' : 'flex-end',
                justifyContent: 'space-between',
                gap: 16,
                marginBottom: 22,
              }}
            >
              <View style={{ maxWidth: 460 }}>
                <Text weight="bold" style={{ color: band.ink, fontSize: 17, letterSpacing: -0.3 }}>
                  Need more mid-month
                </Text>
                <Text style={{ color: band.muted, fontSize: 14, lineHeight: 22, marginTop: 8 }}>
                  One-time packs, no subscription change, and they never expire.
                </Text>
              </View>
              <GhostCta label="Compare every model's cost" href="/models" tone="band" />
            </View>

            <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: isCompact ? 12 : 16 }}>
              {CREDIT_PACKS.map((pack) => (
                <CreditPack key={pack.credits} pack={pack} onPress={() => onBuyCredits?.(pack)} />
              ))}
            </View>

            <InternationalPaymentNotice style={{ marginTop: 20, maxWidth: 520 }} />
          </View>
        </Reveal>
    </PricingSection>
  );
}

export default React.memo(PricingCredits);
